import React, { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonModal,
  IonCard,
  IonCardContent,
  IonIcon,
} from "@ionic/react";
import { notifications } from "ionicons/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { postNotificationsSubscription } from "../../../redux/actions";
import { updateActiveNotifications } from "../../../redux/reducer/NotificationsSlice";
import pushNotificationService from "../../../services/pushNotifications";

import "./ActivateNotifications.css";

const ActivateNotifications = ({
  firstTime = false,
  redirectHome = true,
  setNotificationAccepted,
  showNotificationsModal = true,
  setShowNotificationsModal,
  setNotificationSystemAccepted,
}) => {
  const activeNotificationsState = useSelector(
    (s) => s.notifications.activeNotifications
  );
  const [isOpen, setIsOpen] = useState(false);
  const modal = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();

  // Sync parent state when modal closes
  useEffect(() => {
    if (!isOpen && setShowNotificationsModal) setShowNotificationsModal(false);
  }, [isOpen, setShowNotificationsModal]);

  // Check permission BEFORE opening — prevents flash when already granted
  useEffect(() => {
    if (!showNotificationsModal) return;

    const check = async () => {
      if (!pushNotificationService?.isSupported?.()) {
        setIsOpen(true);
        return;
      }

      const state = await pushNotificationService.checkPermissionState();
      dispatch(updateActiveNotifications(state));

      if (state === "granted") {
        setNotificationSystemAccepted?.(true);
        setNotificationAccepted?.(true);
        // Don't open — already enabled. Release the parent flow (which waits
        // for showNotificationsModal to flip false) since there's nothing to show.
        setShowNotificationsModal?.(false);
      } else if (state === "denied") {
        setNotificationSystemAccepted?.(false);
        // Don't open — denied (separate blocked modal handles this). Release the
        // parent flow so callers waiting on the modal don't deadlock.
        setShowNotificationsModal?.(false);
      } else {
        setIsOpen(true);
      }
    };
    check();
  }, [
    showNotificationsModal,
    dispatch,
    setNotificationSystemAccepted,
    setNotificationAccepted,
    setShowNotificationsModal,
  ]);

  const requestAndRegister = async () => {
    if (!pushNotificationService?.isSupported?.()) return false;

    try {
      const ok = await pushNotificationService.requestPermission();
      const state = await pushNotificationService.checkPermissionState();
      dispatch(updateActiveNotifications(state));

      if (ok) {
        const sub = await pushNotificationService.getSubscription();
        if (sub) dispatch(postNotificationsSubscription(sub));
      }
      return ok;
    } catch {
      dispatch(updateActiveNotifications("denied"));
      return false;
    }
  };

  const handleChoice = async (accept) => {
    let accepted = accept;
    if (accept) accepted = await requestAndRegister();
    setNotificationAccepted?.(accepted);
    setIsOpen(false);
  };

  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      backdropDismiss={false}
      swipeToClose={false}
      className="notify-modal"
      onDidDismiss={() => setIsOpen(false)}
    >
      <IonCard className="nm-card">
        <IonCardContent>
          {/* Close X button */}
          <button
            type="button"
            className="nm-close-x"
            onClick={() => handleChoice(false)}
            aria-label="Chiudi"
          >
            &times;
          </button>

          {/* Centered bell + title */}
          <div className="nm-hero">
            <IonIcon icon={notifications} className="nm-hero-icon" />
            <h2 className="nm-title">
              <strong>Attiva le notifiche push</strong>
            </h2>
          </div>

          {/* Description */}
          <p className="nm-description">
            Ricevi promemoria, aggiornamenti e novità sul tuo percorso.
          </p>

          {/* CTA buttons */}
          <IonButton
            expand="block"
            className="nm-cta"
            onClick={() => handleChoice(true)}
          >
            Attiva notifiche
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            color="medium"
            className="nm-later"
            onClick={() => handleChoice(false)}
          >
            Non ora
          </IonButton>

          {/* Manage notifications link */}
          <button
            type="button"
            className="nm-manage-link"
            onClick={() => {
              setIsOpen(false);
              setNotificationAccepted?.(false);
              history.push("/impostazioni-profilo", { tab: "notifiche" });
            }}
          >
            Gestisci cosa ricevere
          </button>
        </IonCardContent>
      </IonCard>
    </IonModal>
  );
};

export default ActivateNotifications;
